// src/components/HomePage/ServerProtection.jsx
import React, { useState, memo } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import config from '../../config/appConfig';

const ServerProtection = memo(({ onProtectionActivated, openModal }) => {
  const { t, language } = useLanguage();
  const { isAuthenticated, token } = useAuth();
  const { showToast } = useNotification();
  const [ip, setIp] = useState('');
  const [contact, setContact] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ip.trim() || isSubmitting) return;

    if (!isAuthenticated) {
      showToast(
        language === 'ru'
          ? 'Для добавления сервера в защиту необходимо авторизоваться'
          : 'Please log in to add a server to protection registry',
        { type: 'error' }
      );
      if (openModal) {
        openModal('login');
      }
      return;
    }

    try {
      setIsSubmitting(true);
      const authToken = token || localStorage.getItem('authToken');
      const response = await axios.post(
        config.api.protected.addToBlacklist,
        {
          ip: ip.trim(),
          reason: contact.trim() || 'User protection request'
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
          }
        }
      );

      const successMsg = response.data?.message || `Сервер ${ip} добавлен в защиту`;
      showToast(successMsg, { type: 'success' });
      if (onProtectionActivated) {
        onProtectionActivated(successMsg);
      }
      setIp('');
      setContact('');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Ошибка добавления сервера в защиту';
      showToast(errorMsg, { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="mb-12 sm:mb-[85px]" id="defenseSection">
      <div
        className="rounded-[22px] sm:rounded-[28px] p-5 sm:p-[34px_38px] grid grid-cols-1 lg:grid-cols-[1.18fr_0.82fr] gap-7 sm:gap-8 lg:gap-10 items-center border border-white/[0.08] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.95)] bg-[#111319]"
      >
        {/* Left Column: Monolithic Features Block */}
        <div className="flex flex-col gap-4 sm:gap-[22px] justify-center">
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <h2 className="text-2xl sm:text-[2.35rem] font-extrabold text-white tracking-[-0.025em] leading-[1.12]">
              {t('serverProtection') || 'Защита сервера'}
            </h2>
            <p className="text-sm sm:text-[1.02rem] text-[#f5f5f7] opacity-[0.82] leading-[1.55] max-w-[560px] font-normal">
              Добавление вашего сервера в реестр исключений предотвращает запуск любых тестов и сканирований инфраструктуры.
            </p>
          </div>

          {/* Clean minimal checkmarks */}
          <div className="flex flex-col gap-3 sm:gap-[15px]">
            <div className="flex items-start gap-2.5 sm:gap-[13px]">
              <svg className="w-[18px] h-[18px] stroke-[#f0abfc] fill-none shrink-0 mt-[3px]" viewBox="0 0 24 24" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <div className="flex flex-col gap-[2px]">
                <span className="text-sm sm:text-[0.96rem] font-semibold text-white leading-[1.35]">
                  Блокировка RCON и брутфорс-атак
                </span>
                <span className="text-xs sm:text-[0.84rem] text-white/65 leading-[1.4]">
                  Полный запрет сканирования портов и подбора паролей
                </span>
              </div>
            </div>

            <div className="flex items-start gap-2.5 sm:gap-[13px]">
              <svg className="w-[18px] h-[18px] stroke-[#f0abfc] fill-none shrink-0 mt-[3px]" viewBox="0 0 24 24" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <div className="flex flex-col gap-[2px]">
                <span className="text-sm sm:text-[0.96rem] font-semibold text-white leading-[1.35]">
                  Исключение из публичного парсера
                </span>
                <span className="text-xs sm:text-[0.84rem] text-white/65 leading-[1.4]">
                  Скрытие IP-адресов, субдоменов и метаданных из общей выдачи
                </span>
              </div>
            </div>

            <div className="flex items-start gap-2.5 sm:gap-[13px]">
              <svg className="w-[18px] h-[18px] stroke-[#f0abfc] fill-none shrink-0 mt-[3px]" viewBox="0 0 24 24" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <div className="flex flex-col gap-[2px]">
                <span className="text-sm sm:text-[0.96rem] font-semibold text-white leading-[1.35]">
                  Мгновенный иммунитет StormBlack Core
                </span>
                <span className="text-xs sm:text-[0.84rem] text-white/65 leading-[1.4]">
                  Немедленная отмена любых запущенных стресс-тестов на уровне ядра
                </span>
              </div>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 pt-3 border-t border-white/10 text-[0.78rem] sm:text-[0.82rem] text-white/65 font-medium mt-[2px]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2ed573] shadow-[0_0_7px_#2ed573] shrink-0" />
            <span>Внесение в реестр бесплатно и действует бессрочно</span>
          </div>
        </div>

        {/* Right Column: Registry Box Form */}
        <div
          className="rounded-[18px] sm:rounded-[22px] p-4 sm:p-[26px_28px] border border-white/[0.12] shadow-[0_16px_36px_rgba(0,0,0,0.85)] flex flex-col justify-center bg-[#0c0e14]"
        >
          <div>
            <h3 className="text-lg sm:text-[1.25rem] font-extrabold text-white tracking-[-0.015em] mb-1">Реестр защиты сервера</h3>
            <p className="text-xs sm:text-[0.85rem] text-white/65 mb-3.5 sm:mb-[18px] leading-[1.4]">Внесите адрес для автоматической блокировки всех действий</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="flex flex-col gap-1.5 mb-3 sm:mb-[14px]">
              <label className="text-xs sm:text-[0.82rem] font-semibold text-white block">
                IP-адрес или домен сервера
              </label>
              <input
                type="text"
                required
                placeholder="mc.myserver.org:25565"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                className="w-full bg-[#07080c] border border-white/[0.14] rounded-[16px] sm:rounded-[18px] px-3.5 sm:px-4 py-2.5 sm:py-3 text-[16px] sm:text-[0.95rem] font-mono text-white placeholder-white/35 focus:outline-none focus:border-white focus:shadow-[0_0_0_1px_rgba(255,255,255,0.8),0_0_16px_rgba(255,255,255,0.15)] transition-all"
              />
            </div>

            <div className="flex flex-col gap-1.5 mb-3.5 sm:mb-[14px]">
              <label className="text-xs sm:text-[0.82rem] font-semibold text-white block">
                Контакт владельца (Telegram / Discord)
              </label>
              <input
                type="text"
                required
                placeholder="@username"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className="w-full bg-[#07080c] border border-white/[0.14] rounded-[16px] sm:rounded-[18px] px-3.5 sm:px-4 py-2.5 sm:py-3 text-[16px] sm:text-[0.95rem] font-mono text-white placeholder-white/35 focus:outline-none focus:border-white focus:shadow-[0_0_0_1px_rgba(255,255,255,0.8),0_0_16px_rgba(255,255,255,0.15)] transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-white text-black font-extrabold text-[0.96rem] py-[13px] px-5 rounded-full mt-1 transition-all duration-200 shadow-[0_4px_20px_rgba(255,255,255,0.15)] flex items-center justify-center gap-2 ${
                isSubmitting
                  ? 'opacity-60 cursor-not-allowed'
                  : 'hover:bg-[#ececee] hover:scale-[1.01] active:scale-[0.98] cursor-pointer'
              }`}
            >
              <span>
                {isSubmitting
                  ? (language === 'ru' ? 'Добавление...' : 'Adding...')
                  : (language === 'ru' ? 'Активировать защиту' : 'Activate protection')}
              </span>
              {!isSubmitting && (
                <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current fill-none" strokeWidth="2.8">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
});

export default ServerProtection;